<template>
  <div class="functionalTestResults">
    <div v-if="test.daylight_control_enabled">
      <h2 class="bold">{{ $t("tests.functional.daylight_control.title") }}</h2>

      <yes-no-answer-box
        :question="$t('tests.functional.daylight_control.question')"
        :answer="
          daylightControlResultAnswerAndComment &&
          daylightControlResultAnswerAndComment.answer
        "
        :comment="
          daylightControlResultAnswerAndComment &&
          daylightControlResultAnswerAndComment.comment
        "
      />

      <div v-if="test.daylight_zones_enabled">
        <h2 class="bold mt-10">{{ $t("evaluation.zoning.title") }}</h2>

        <p class="body1 my-4">{{ $t("evaluation.zoning.zones_amount") }}</p>

        <span
          class="pa-1 body-1"
          :class="
            test.actual_number_of_zones >= test.required_number_of_zones ||
            test.required_number_of_zones_unknown
              ? 'greenBox'
              : 'redBox'
          "
        >
          {{ test.actual_number_of_zones }}</span
        >
        <h4 class="mt-4">{{ $t("evaluation.zoning.specified") }}</h4>
        <span class="body1">
          {{
            test.required_number_of_zones_unknown
              ? $t("evaluation.zoning.unknown")
              : test.required_number_of_zones
          }}
        </span>

        <!--Cannot find a comment for number of zones-->

        <yes-no-answer-box
          :question="$t('tests.functional.daylight_zones.questions.are_dimmed')"
          :answer="
            luminairesResponsivenessAnswerAndComment &&
            luminairesResponsivenessAnswerAndComment.answer
          "
          :comment="
            luminairesResponsivenessAnswerAndComment &&
            luminairesResponsivenessAnswerAndComment.comment
          "
        />

        <yes-no-answer-box
          :question="$t('tests.functional.daylight_zones.questions.can_dim')"
          :answer="
            luminairesZoneSwitchOffAnswerAndComment &&
            luminairesZoneSwitchOffAnswerAndComment.answer
          "
          :comment="
            luminairesZoneSwitchOffAnswerAndComment &&
            luminairesZoneSwitchOffAnswerAndComment.comment
          "
        />
      </div>
    </div>

    <div v-if="!test.daylight_control_enabled">
      <h2 class="bold mt-10 mb-6">
        {{ $t("tests.functional.daylight_control.title") }}
      </h2>
      <span class="pa-1 body-1 greyBox">
        {{ $t("evaluation.not_required") }}
      </span>
      <div class="my-4">
        <h4>{{ $t("evaluation.result.comment") }}</h4>
        <span class="body-1 text-grey-darker">
          {{
            test.daylight_control_disabled_comment !== ""
              ? test.daylight_control_disabled_comment
              : " - "
          }}
        </span>
      </div>
    </div>

    <div v-if="!test.daylight_zones_enabled">
      <h2 class="bold mt-10 mb-7">{{ $t("evaluation.zoning.title") }}</h2>
      <span class="pa-1 my-4 body-1 greyBox">
        {{ $t("evaluation.not_required") }}
      </span>
      <div class="my-4">
        <h4>{{ $t("evaluation.result.comment") }}</h4>
        <span class="body-1 text-grey-darker">
          {{
            test.daylight_zones_disabled_comment !== ""
              ? test.daylight_zones_disabled_comment
              : " - "
          }}
        </span>
      </div>
    </div>

    <div v-if="test.motion_detectors_enabled">
      <h2 class="bold mt-10">
        {{ $t("tests.functional.motion_detectors.title") }}
      </h2>
      <div>
        <div v-if="luminairesLightsOnAutomaticAnswerAndComment">
          <yes-no-answer-box
            :question="
              $t('tests.functional.motion_detectors.questions.automatic')
            "
            :answer="
              luminairesLightsOnAutomaticAnswerAndComment &&
              luminairesLightsOnAutomaticAnswerAndComment.answer
            "
            :comment="
              luminairesLightsOnAutomaticAnswerAndComment &&
              luminairesLightsOnAutomaticAnswerAndComment.comment
            "
          />
        </div>
        <div v-else>
          <yes-no-answer-box
            :question="$t('tests.functional.motion_detectors.questions.manual')"
            :answer="
              luminairesLightsOnBySwitchAnswerAndComment &&
              luminairesLightsOnBySwitchAnswerAndComment.answer
            "
            :comment="
              luminairesLightsOnBySwitchAnswerAndComment &&
              luminairesLightsOnBySwitchAnswerAndComment.comment
            "
          />
        </div>

        <yes-no-answer-box
          :question="
            $t('tests.functional.motion_detectors.questions.detectors')
          "
          :answer="
            detectorsSensitivityAnswerAndComment &&
            detectorsSensitivityAnswerAndComment.answer
          "
          :comment="
            detectorsSensitivityAnswerAndComment &&
            detectorsSensitivityAnswerAndComment.comment
          "
        />

        <yes-no-answer-box
          :question="
            $t('tests.functional.motion_detectors.questions.turn_off', {
              delay: test.light_off_delay,
            })
          "
          :answer="
            lightsOffAutomaticallyAnswerAndComment &&
            lightsOffAutomaticallyAnswerAndComment.answer
          "
          :comment="
            lightsOffAutomaticallyAnswerAndComment &&
            lightsOffAutomaticallyAnswerAndComment.comment
          "
        />
      </div>
    </div>

    <div v-if="!test.motion_detectors_enabled">
      <h2 class="bold mt-10 mb-7">
        {{ $t("tests.functional.motion_detectors.title") }}
      </h2>
      <span class="pa-1 my-4 body-1 greyBox">
        {{ $t("evaluation.not_required") }}
      </span>
      <div class="my-4">
        <h4>{{ $t("evaluation.result.comment") }}</h4>
        <span class="body-1 text-grey-darker">
          {{
            test.motion_detectors_disabled_comment !== ""
              ? test.motion_detectors_disabled_comment
              : " - "
          }}
        </span>
      </div>
    </div>
    <guidance-button page="evaluation.functional_test" />
  </div>
</template>

<script>
import GuidanceButton from "@/components/guidance/GuidanceButton";
import YesNoAnswerBox from "./YesNoAnswerBox";

export default {
  name: "evaluator-functional-test-results",
  components: { YesNoAnswerBox, GuidanceButton },
  props: ["test"],
  computed: {
    daylightControlResultAnswerAndComment() {
      return this.getTestResultAndCommentByIdentifier("daylight_control");
    },
    luminairesResponsivenessAnswerAndComment() {
      return this.getTestResultAndCommentByIdentifier(
        "luminaires_zone_responsive"
      );
    },
    luminairesZoneSwitchOffAnswerAndComment() {
      return this.getTestResultAndCommentByIdentifier(
        "luminaires_zone_switch_off"
      );
    },
    luminairesLightsOnAutomaticAnswerAndComment() {
      return this.getTestResultAndCommentByIdentifier(
        "lights_on_automatically"
      );
    },
    luminairesLightsOnBySwitchAnswerAndComment() {
      return this.getTestResultAndCommentByIdentifier("lights_on_by_switch");
    },
    detectorsSensitivityAnswerAndComment() {
      return this.getTestResultAndCommentByIdentifier(
        "detector_sensitivity_sufficient"
      );
    },
    lightsOffAutomaticallyAnswerAndComment() {
      return this.getTestResultAndCommentByIdentifier(
        "lights_off_automatically"
      );
    },
  },
  methods: {
    getTestResultAndCommentByIdentifier(identifier) {
      const testResult = this.test.functional_test_results.find(
        (result) => result.question_identifier === identifier
      );

      if (!testResult) {
        return undefined;
      }

      return {
        answer: testResult.answer,
        comment: testResult.comment !== "" ? testResult.comment : " - ",
      };
    },
  },
};
</script>

<style lang="scss">
.functionalTestResults {
  .greenBox {
    background-color: #28a078 !important;
    border-radius: 4px;
  }

  .redBox {
    background-color: red;
    border-radius: 4px;
  }

  .greyBox {
    background-color: #7f7f7f;
    border-radius: 4px;
  }
}
</style>
